import { publicBlogPath, resolveBlogImageUrl, rewritePreviewMedia } from '../lib/resolveBlogImageUrl';
import type { BlogPostFormData } from '../types';

function minutesFromHtml(html: string): number {
    const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return Math.max(1, Math.round(text.split(' ').filter(Boolean).length / 200));
}

export function BlogPostPreview({
    form,
    typeName,
    typeColor,
}: {
    form: BlogPostFormData;
    typeName?: string | null;
    typeColor?: string | null;
}) {
    const hero = resolveBlogImageUrl(form.main_image_url);
    const body = rewritePreviewMedia(form.body_html || '');
    const badgeColor = typeColor || '#15A8DC';
    const path = publicBlogPath(form.slug || '…', form.locale);
    const credit = form.hero_credit_username.trim();

    return (
        <article
            data-preview="blog-post"
            className="rounded-2xl border border-zinc-200 bg-[#f4f7fb] text-zinc-900 shadow-lg overflow-hidden"
        >
            <div className="px-4 py-2 bg-zinc-800 text-[11px] text-zinc-200 flex items-center justify-between gap-2">
                <span>Vista previa de la landing</span>
                <span className="text-zinc-400 truncate">{path}</span>
            </div>
            {hero && form.main_image_position !== 'side' && (
                <figure className="m-0 bg-zinc-900">
                    <img src={hero} alt="" className="w-full max-h-56 object-cover" />
                    {credit && (
                        <figcaption className="px-3 py-1.5 text-[11px] text-zinc-400 bg-zinc-900">
                            Foto: {credit} · Comunidad AiPeces
                        </figcaption>
                    )}
                </figure>
            )}
            <div className="p-5 sm:p-6 max-w-[720px] mx-auto space-y-4">
                <header>
                    {typeName && (
                        <span
                            className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full mb-2"
                            style={{ backgroundColor: `${badgeColor}1f`, color: badgeColor, border: `1px solid ${badgeColor}33` }}
                        >
                            {typeName}
                        </span>
                    )}
                    <h1 className="text-[1.7rem] leading-tight font-extrabold m-0" style={{ fontFamily: 'Nunito, system-ui, sans-serif' }}>
                        {form.title || 'Título del artículo'}
                    </h1>
                    <p className="text-xs text-zinc-500 mt-2 mb-0">
                        {minutesFromHtml(form.body_html)} min de lectura
                    </p>
                </header>
                {form.excerpt && (
                    <p className="text-[1.05rem] leading-relaxed text-zinc-600 m-0">{form.excerpt}</p>
                )}
                {hero && form.main_image_position === 'side' && (
                    <figure className="m-0">
                        <img src={hero} alt="" className="w-full max-h-48 object-cover rounded-xl" />
                        {credit && (
                            <figcaption className="mt-1.5 text-[11px] text-zinc-500">
                                Foto: {credit} · Comunidad AiPeces
                            </figcaption>
                        )}
                    </figure>
                )}
                <div
                    className="blog-admin-preview-body"
                    dangerouslySetInnerHTML={{
                        __html: body || '<p>Escribe el cuerpo a la izquierda para verlo aquí.</p>',
                    }}
                />
            </div>
            <style>{`
                .blog-admin-preview-body { font-family: Nunito, system-ui, sans-serif; font-size: 15px; line-height: 1.65; color: #1e293b; }
                .blog-admin-preview-body h1 { font-size: 1.35rem; font-weight: 800; margin: 0 0 0.75rem; }
                .blog-admin-preview-body h2 { font-size: 1.2rem; font-weight: 800; margin: 1.5rem 0 0.5rem; }
                .blog-admin-preview-body h3 { font-size: 1.05rem; font-weight: 800; margin: 1.25rem 0 0.4rem; }
                .blog-admin-preview-body p { margin: 0 0 0.85rem; }
                .blog-admin-preview-body a { color: #0284c7; text-decoration: underline; }
                .blog-admin-preview-body ul, .blog-admin-preview-body ol { margin: 0 0 0.85rem; padding-left: 1.25rem; }
                .blog-admin-preview-body li { margin: 0.2rem 0; }
                .blog-admin-preview-body img { max-width: 100%; height: auto; border-radius: 0.75rem; margin: 0.5rem 0; }
                .blog-admin-preview-body table { width: 100%; border-collapse: collapse; font-size: 0.85rem; margin: 0.75rem 0; display: block; overflow-x: auto; }
                .blog-admin-preview-body th, .blog-admin-preview-body td { border: 1px solid #e4e4e7; padding: 0.4rem 0.55rem; text-align: left; }
                .blog-admin-preview-body th { background: #eef2f6; }
                .blog-admin-preview-body details { border: 1px solid #e4e4e7; border-radius: 0.75rem; padding: 0.6rem 0.8rem; margin: 0.5rem 0; background: #fff; }
                .blog-admin-preview-body summary { font-weight: 700; cursor: pointer; }
                .blog-admin-preview-body blockquote { border-left: 3px solid #15A8DC; margin: 0.75rem 0; padding: 0.2rem 0.8rem; color: #475569; }
                .blog-admin-preview-body [data-blog-widget] { border: 1px dashed #94a3b8; border-radius: 0.75rem; padding: 0.75rem; background: #fff; margin: 0.75rem 0; }
            `}</style>
        </article>
    );
}
